import React from "react";
import { useNavigate } from "react-router-dom";
import Modal from "react-modal";
import "../home/home.css";
import "./HypeScanner.css";
import CategoryHypeCharts from "./CategoryHypeCharts";

Modal.setAppElement('#root');

/* --- Broad category matcher (same rules) --- */
const BROAD_WORDS = ["Layer", "Portfolio", "Holdings", "Ecosystem", "Made in USA", "Smart Contract Platform", 
  "Launchpad", "Index"
];
const BROAD_RE = new RegExp(`\\b(${BROAD_WORDS.map(w => w.replace(/\s+/g, "\\s+")).join("|")})\\b`, "i");
const isBroadCategory = (name = "") => BROAD_RE.test(String(name));

/* ---- Reusable CategorySelect (unchanged; it will receive filtered options) ---- */
function CategorySelect({
  value,
  onChange,
  options,           // [{ name, pointCount, slug }]
  placeholder = "All categories…",
  className = "filter-select",
  showCounts = true,
}) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState(value || "");
  const [highlight, setHighlight] = React.useState(0);
  const wrapRef = React.useRef(null);
  const inputRef = React.useRef(null);

  const list = React.useMemo(() => {
    const q = (query || "").toLowerCase().trim();
    const base = options || [];
    if (!q) return base.slice(0, 200);
    return base.filter(c => c.name.toLowerCase().includes(q)).slice(0, 200);
  }, [options, query]);

  React.useEffect(() => { setQuery(value || ""); }, [value]);

  React.useEffect(() => {
    function onDocClick(e) {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  function selectCategory(name) {
    onChange?.(name);
    setQuery(name || "");
    setOpen(false);
  }
  function clearSelection() {
    onChange?.("");
    setQuery("");
    setOpen(false);
    inputRef.current?.focus();
  }
  function onKeyDown(e) {
    if (!open && (e.key === "ArrowDown" || e.key === "Enter")) { setOpen(true); return; }
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight(h => Math.min(h + 1, Math.max(list.length - 1, 0))); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); const item = list[highlight]; if (item) selectCategory(item.name); }
    else if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
  }

  return (
    <div ref={wrapRef} style={{ position: "relative", width: 280 }}>
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <input
          ref={inputRef}
          className={className}
          placeholder={placeholder}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          style={{ width: "100%", padding: "6px 10px", borderRadius:"6px",
                   border:"1px solid #555", background:"#111", color:"#fff" }}
        />
        {value && (
          <button
            onClick={clearSelection}
            title="Clear"
            style={{ background:"transparent", border:"1px solid #555",
                     color:"#fff", padding:"4px 8px", borderRadius:"6px", cursor:"pointer" }}
          >
            ✕
          </button>
        )}
      </div>

      {open && (
        <div
          style={{
            position: "absolute", left: 0, right: 0, zIndex: 50,
            background: "#111", border: "1px solid #555", borderRadius: 8,
            marginTop: 6, maxHeight: 320, overflowY: "auto",
            boxShadow: "0 8px 24px rgba(0,0,0,0.4)"
          }}
        >
          <div
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => selectCategory("")}
            style={{
              padding: "8px 10px", cursor: "pointer",
              background: value ? "transparent" : "#1b1b1b",
              fontWeight: value ? 400 : 600
            }}
          >
            All
          </div>
          {list.length === 0 && (
            <div style={{ padding: "8px 10px", opacity: 0.7 }}>No matches</div>
          )}
          {list.map((c, idx) => (
            <div
              key={c.slug || c.name}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setHighlight(idx)}
              onClick={() => selectCategory(c.name)}
              style={{
                padding: "8px 10px", cursor: "pointer",
                background: idx === highlight ? "#1e293b" : "transparent",
                display: "flex", justifyContent:"space-between", gap: 8
              }}
            >
              <span>{c.name}</span>
              {showCounts && (
                <span style={{ opacity: 0.7 }}>{c.pointCount ?? 0}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const PAGE_SIZE = 50;

export default function HypeScanner() {
  const isLocal = window.location.hostname === "localhost";
  const baseUrl = isLocal ? "http://localhost:5000" : "https://www.trendscout.club";
  const [trendingCats, setTrendingCats] = React.useState([]);
  const [trendHours, setTrendHours] = React.useState(24);
  const navigate = useNavigate();

  // categories
  const [categories, setCategories] = React.useState([]);
  const [selectedCategory, setSelectedCategory] = React.useState("");
  const [showBroad, setShowBroad] = React.useState(false); // NEW: hide umbrella cats by default

  // data
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(null);

  // filters
  const [q, setQ] = React.useState("");
  const [minScore, setMinScore] = React.useState(0);
  const [days, setDays] = React.useState(14);
  const [stage, setStage] = React.useState(["PRE_HYPE_TRIGGER", "EARLY_WATCH", "WAKEUP_SPIKE",]);
  const [sort, setSort] = React.useState("recent"); // recent | score | stage
  const [limit, setLimit] = React.useState(500);    // pull enough to sort globally on server

  // pagination (client-side)
  const [page, setPage] = React.useState(1);

  // chart modal
  const [isChartOpen, setIsChartOpen] = React.useState(false);
  const [chartSymbol, setChartSymbol] = React.useState("");

  const openChart = (symbol) => {
    setChartSymbol(`BINANCE:${symbol}`);
    setIsChartOpen(true);
  };
  const closeChart = () => setIsChartOpen(false);

  // fetch categories on mount
  React.useEffect(() => {
    (async () => {
      try {
        const url = `${baseUrl}/api/categories`;
        const r = await fetch(url);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        setCategories(j.items || []);
      } catch (e) {
        console.error("categories load error", e);
      }
    })();
  }, [baseUrl]);

  React.useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${baseUrl}/api/categories/trending?hours=${trendHours}&limit=5`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        setTrendingCats(j.items || []);
      } catch (e) {
        console.warn('trending categories load error', e);
        setTrendingCats([]); // fallback safe
      }
    })();
  }, [baseUrl, trendHours]);

  // derive visible categories + hidden count
  const visibleCategories = React.useMemo(() => {
    if (showBroad) return categories;
    return (categories || []).filter(c => !isBroadCategory(c.name));
  }, [categories, showBroad]);

  const hiddenCount = React.useMemo(
    () => (categories || []).reduce((n, c) => n + (isBroadCategory(c.name) ? 1 : 0), 0),
    [categories]
  );



  // build query & load tokens
  async function load() {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (minScore) params.set("minScore", String(minScore));
      if (days) params.set("days", String(days));
      if (limit) params.set("limit", String(limit));
      if (sort) params.set("sort", sort);
      if (stage?.length) params.set("stage", stage.join(","));
      if (selectedCategory) params.set("category", selectedCategory);

      const url = `${baseUrl}/api/hype/candidates?${params.toString()}`;
      const r = await fetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setItems(j.items || []);
      setPage(1);
    } catch (e) {
      console.error(e);
      setError(e.message || "Failed to load");
    } finally {
      setLoading(false);
    }
  }

  // instant filters: debounce 350ms
  React.useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, minScore, days, stage, sort, selectedCategory, limit]);

  // Stage checkbox toggle
  const onToggleStage = (name) => {
    setStage(prev => prev.includes(name) ? prev.filter(x => x !== name) : [...prev, name]);
  };

  const fmt = (n, d=2) => Number.isFinite(+n) ? Number(n).toFixed(d) : "—";
  const displaySymbol = (it) => (it.base || it.symbol || "").replace(/USDT$/i, "").toUpperCase();

  // 🔝 Top 5 trending categories (from visible list)
  const top5 = React.useMemo(() =>
    [...visibleCategories].sort((a,b) => (b.pointCount||0) - (a.pointCount||0)).slice(0,5),
    [visibleCategories]
  );

  // table pagination
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageItems = items.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE);

  const goCategory = (name) => navigate(`/categories/${encodeURIComponent(name)}`);
  const goAllCategories = () => navigate(`/categories`);

  return (
    <div className="macro-container">
      <div className="macro-section">
        <CategoryHypeCharts initialDays={30} maxRsiCategories={14} />
        {/* 🔥 Trending last 24h */}
      {trendingCats.length > 0 && (
        <div style={{ display:"flex", gap:8, margin:"10px 0", flexWrap:"wrap", alignItems:"center" }}>
          <span style={{ opacity:0.8 }}>🔥 Trending {trendHours}h:</span>

                {trendingCats.map(c => (
                  <button
                    key={c.slug || c.name}
                    className="pill pill-yellow"
                    onClick={() => goCategory(c.name)}
                    title={`${c.name} — ${c.recentCount} hits (${c.pointCount} in 7d)`}
                    style={{ cursor: 'pointer' }}
                  >
                    {c.name} ({c.recentCount})
                  </button>
                ))}

          <button className="pill" onClick={goAllCategories} title="View all categories">
            View all →
          </button>
        </div>
      )}

        {/* 🔝 Top 5 trending categories + toggle for broad categories */}
        {top5.length > 0 && (
          <div style={{ display: "flex", gap: 8, margin: "10px 0", flexWrap: "wrap", alignItems: "center" }}>
            {top5.map(c => (
              <button
                key={c.slug}
                className="pill pill-yellow"
                onClick={() => goCategory(c.name)}
                title={`${c.name} — ${c.pointCount||0} pts (7d)`}
                style={{ cursor:"pointer" }}
              >
                {c.name} ({c.pointCount ?? 0})
              </button>
            ))}
            <button className="pill" onClick={goAllCategories} title="View all categories">
              View all →
            </button>

            {/* Toggle + hidden count */}
            <label className="custom-checkbox" style={{ marginLeft: 12, display:"flex", alignItems:"center", gap:8, cursor:"pointer" }}>
              <input type="checkbox" checked={showBroad} onChange={() => setShowBroad(v => !v)} />
              <span className="checkmark" />
              Show broad categories
            </label>
            {!showBroad && hiddenCount > 0 && (
              <span style={{ opacity:0.75 }}>
                (Hiding {hiddenCount} umbrella categories: {BROAD_WORDS.join(", ")})
              </span>
            )}
          </div>
        )}

        {/* Filters */}
        <div className="hype-filter-section">
          {/* Category filter (uses visible list so it mirrors the toggle) */}
          <div className="filter-group">
            <span className="filter-label">Category</span>
            <CategorySelect
              value={selectedCategory}
              onChange={setSelectedCategory}
              options={visibleCategories}
              placeholder="Type to search categories…"
            />
          </div>

          <div className="filter-group">
            <span className="filter-label">Search</span>
            <input
              type="text"
              placeholder="e.g. ARB, TAO, 'breakout'"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="search-input"
              style={{ padding:"6px 10px", borderRadius:"6px", border:"1px solid #555", background:"#111", color:"#fff", width:"220px" }}
            />
          </div>

            <div className="filter-group">
              <span className="filter-label">Stage</span>
              {["PRE_HYPE_TRIGGER", "EARLY_WATCH", "WAKEUP_SPIKE", "LATE/EXTENDED"].map(st => (
                <label key={st} className="custom-checkbox">
                  <input
                    type="checkbox"
                    checked={stage.includes(st)}
                    onChange={() => onToggleStage(st)}
                  />
                  <span className="checkmark" />
                  {st}
                </label>
              ))}
            </div>


          <div className="filter-group">
            <span className="filter-label">Min Score</span>
            <input
              type="number"
              value={minScore}
              onChange={e => setMinScore(Number(e.target.value || 0))}
              className="filter-number"
              min={0} step={0.5}
              style={{ padding:"6px 10px", borderRadius:"6px", border:"1px solid #555", background:"#111111ff", color:"#fff", width:"150px" }}
            />
          </div>

          <div className="filter-group">
            <span className="filter-label">Days</span>
            <input
              type="number"
              value={days}
              onChange={e => setDays(Number(e.target.value || 14))}
              className="filter-number"
              min={1} max={365}
              style={{ padding:"6px 10px", borderRadius:"6px", border:"1px solid #555", background:"#111", color:"#fff", width:"150px" }}
            />
          </div>

          <div className="filter-group">
            <span className="filter-label">Sort</span>
            <select value={sort} onChange={e => setSort(e.target.value)} className="filter-select">
              <option value="recent">Most Recent</option>
              <option value="score">Highest Score</option>
              <option value="stage">Stage</option>
            </select>
          </div>

          <div className="filter-group">
            <span className="filter-label">Pull (max)</span>
            <input
              type="number"
              value={limit}
              onChange={e => setLimit(Number(e.target.value || 500))}
              className="filter-number"
              min={50} max={1000} step={50}
              style={{ padding:"6px 10px", borderRadius:"6px", border:"1px solid #555", background:"#111", color:"#fff", width:"150px" }}
            />
          </div>
        </div>

        {/* Loading / Errors */}
        {loading && <div className="loading-banner">Loading…</div>}
        {error && <div className="error-banner">{error}</div>}
        {!loading && !error && items.length === 0 && (
          <div className="subtle-note">No candidates found for the selected filters.</div>
        )}

        {/* Results: TABLE + pagination */}
        {!loading && !error && items.length > 0 && (
          <>
            <div className="table-wrap">
              <table className="hype-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>Stage</th>
                    <th>Score</th>
                    <th>Hits (24h)</th>
                    <th>Caught Price</th>
                    <th>Volatility</th>
                    <th>Categories</th>
                    <th>Last Updated</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((it, idx) => {
                    const s = it.signals || {};
                    const last = it.lastSeenAt || it.scannedAt || it.createdAt;
                    const stageClass = (st) => {
                    const s = String(st || "");
                    if (s === "PRE_HYPE_TRIGGER" || s === "WAKEUP_SPIKE") return "pill pill-green";
                    if (s.includes("LATE")) return "pill pill-red";
                    return "pill pill-yellow"; // EARLY_WATCH and anything else
                  };
                    return (
                      <tr key={it._id || idx}>
                        <td onClick={() => openChart(it.symbol)} title="Open TradingView" style={{ cursor:"pointer", fontWeight:700 }}>
                          {displaySymbol(it)}
                        </td>
                        <td>
                        <td>
                          <span className={stageClass(it.stage)}>
                            {String(it.stage || "")}
                          </span>
                        </td>
                        </td>
                        <td>{fmt(it.score,2)}</td>
                        <td>{it.count ?? 0}</td>
                        <td>${Number.isFinite(it.caughtPrice) ? fmt(it.caughtPrice,4) : "—"}</td>
                        <td>{Number.isFinite(s.bbw) ? fmt(s.bbw,4) : "—"}</td>
                        <td>
                          {Array.isArray(it.categories) && it.categories.length > 0
                            ? it.categories.slice(0,3).join(", ")
                            : "—"}
                        </td>
                        <td>{last ? new Date(last).toLocaleString() : "—"}</td>
                        <td>
                          <button className="hype-button" onClick={() => openChart(it.symbol)}>Chart</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="pagination">
              <button disabled={page<=1} onClick={() => setPage(p => p-1)}>&lt;</button>
              <span>Page {page} / {totalPages}</span>
              <button disabled={page>=totalPages} onClick={() => setPage(p => p+1)}>&gt;</button>
            </div>
          </>
        )}
      </div>

      {/* Chart modal */}
      <Modal
        isOpen={isChartOpen}
        onRequestClose={closeChart}
        contentLabel="TradingView Chart"
        style={{
          content: {
            width: '98%', height: '95%', margin: 'auto', backgroundColor: '#111',
            borderRadius: '12px', padding: '10px', inset: 'unset',
          },
          overlay: { backgroundColor: 'rgba(0, 0, 0, 0.85)', zIndex: 1000 }
        }}
      >
        <button onClick={closeChart} style={{ float: 'right', background: 'none', color: '#fff' }}>
          ✖ Close
        </button>
        {chartSymbol && (
          <iframe
            src={`https://www.tradingview.com/widgetembed/?symbol=${chartSymbol}&interval=60&hidesidetoolbar=1&hideideas=1&theme=dark&studies=RSI@tv-basicstudies,MACD@tv-basicstudies`}
            style={{ width: '100%', height: '90%' }}
            frameBorder="0"
            title="TradingView Chart"
          />
        )}
      </Modal>
                <div style={{
            display: "flex",
            justifyContent: "flex-end",
            marginTop: 12,
            fontSize: 12,
            color: "#9aa4b2"
          }}>
            <span>
              Data provided by{" "}
              <a
                href="https://www.coingecko.com/en/api"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#93c5fd", textDecoration: "underline" }}
                aria-label="CoinGecko API (opens in a new tab)"
              >
                CoinGecko
              </a>
            </span>
          </div>
    </div>
  );
}
